import { permissionEngine } from '@/core/permissions/permissionEngine';
import { useAuth } from '@/core/auth/AuthContext';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
 ChevronLeft, 
 Edit3, 
 CheckCircle2, 
 FileText, 
 Calendar, 
 Layout, 
 User, 
 Clock, 
 ShieldCheck,
 Package,
 Info,
 MoreVertical
} from 'lucide-react';
import { useRequestsDetail } from './hooks/useRequestsDetail';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cn } from '@/utils/cn';

export function RequestDetailPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'requests.view');

 const navigate = useNavigate();
 const { request, isLoading, isApproving, approve } = useRequestsDetail();
 const [confirmOpen, setConfirmOpen] = useState(false);

 if (isLoading) {
 return (
 <div className="mx-auto max-w-5xl animate-pulse space-y-4">
 <div className="h-20 rounded-md bg-white border border-slate-100" />
 <div className="grid gap-4 md:grid-cols-12">
 <div className="md:col-span-8 h-96 rounded-md bg-white border border-slate-100" />
 <div className="md:col-span-4 h-64 rounded-md bg-white border border-slate-100" />
 </div>
 </div>
 );
 }

 if (!request) return <div className="p-10 text-center">Không tìm thấy yêu cầu</div>;

 const statusMap = {
 pending: { label: 'Chờ duyệt', color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50' },
 requested: { label: 'Chờ duyệt', color: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50' },
 approved: { label: 'Đã duyệt', color: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50' },
 issued: { label: 'Đã xuất kho', color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
 completed: { label: 'Hoàn thành', color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
 rejected: { label: 'Từ chi', color: 'bg-red-500', text: 'text-red-600', bg: 'bg-red-50' },
 };

 const priorityMap = {
 low: { label: 'Thấp', color: 'bg-slate-50 text-slate-500' },
 normal: { label: 'Thường', color: 'bg-blue-50 text-blue-600' },
 high: { label: 'Cao', color: 'bg-orange-50 text-orange-600' },
 urgent: { label: 'Khẩn', color: 'bg-red-50 text-red-600' },
 };

 return (
 <div className="mx-auto max-w-5xl space-y-4 pb-12">
 {/* Header */}
 <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white p-3">
 <div className="flex items-center gap-3 min-w-0">
 <button
 onClick={() => navigate('/requests')}
 className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
 >
 <ChevronLeft className="h-5 w-5" />
 </button>
 <div className="min-w-0">
 <div className="flex items-center gap-2">
 <h1 className="sm:text-sm text-xs text-slate-900 truncate">{request.title}</h1>
 <span className={cn('px-2 py-0.5 rounded whitespace-nowrap', statusMap[request.status_code as keyof typeof statusMap].bg, statusMap[request.status_code as keyof typeof statusMap].text)}>
 {statusMap[request.status_code as keyof typeof statusMap].label}
 </span>
 </div>
 <p className="sm:text-sm text-xs text-slate-500">REQ-{request.id.substring(0, 8)} · {request.project?.name || '-'}</p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 {request.status_code === 'pending' && (
 <button
 onClick={() => setConfirmOpen(true)}
 className="flex h-10 items-center gap-2 rounded-md bg-emerald-700 px-4 sm:text-sm text-xs text-white -emerald-700/10 hover:bg-emerald-800 transition-all active:scale-95"
 >
 <CheckCircle2 className="h-4 w-4" />
 <span className="hidden smớinline">Phê duyệt</span>
 </button>
 )}
 <button
 onClick={() => navigate(`/requests/edit/${request.id}`)}
 className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
 >
 <Edit3 className="h-4 w-4" />
 </button>
 </div>
 </div>

 <div className="grid gap-4 md:grid-cols-12">
 {/* Main Info */}
 <div className="md:col-span-8 space-y-4">
 <section className="rounded-md border border-slate-100 bg-white p-4 space-y-6">
 <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800">
 <Info className="h-4 w-4 text-emerald-600" />
 <span>Chi tiết yêu cầu</span>
 </div>

 <div className="grid gap-6 sm:grid-cols-2">
 <div className="space-y-1">
 <span className="text-slate-400 tracking-widest">Dự án</span>
 <p className="sm:text-sm text-xs text-slate-700">{request.project?.name || '-'}</p>
 </div>
 <div className="space-y-1">
 <span className="text-slate-400 tracking-widest">Khu vực / Zone</span>
 <p className="sm:text-sm text-xs text-slate-700">{request.zone?.name || '-'}</p>
 </div>
 <div className="space-y-1">
 <span className="text-slate-400 tracking-widest">Người yêu cầu</span>
 <div className="flex items-center gap-2">
 <div className="h-5 w-5 rounded-full bg-slate-200" />
 <p className="sm:text-sm text-xs text-slate-700">{request.requester?.full_name || '-'}</p>
 </div>
 </div>
 <div className="space-y-1">
 <span className="text-slate-400 tracking-widest">Ngày cần vật tư</span>
 <p className="sm:text-sm text-xs text-slate-700">{request.needed_date ? new Date(request.needed_date).toLocaleDateString('vi-VN') : '-'}</p>
 </div>
 <div className="sm:col-span-2 space-y-1">
 <span className="text-slate-400 tracking-widest">Giải trình mục ích</span>
 <p className="sm:text-sm text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-md">
 {request.description || 'Khôngốcó mô tả chi tiết.'}
 </p>
 </div>
 </div>
 </section>

 {/* Items Table */}
 <section className="rounded-md border border-slate-100 bg-white overflow-hidden">
 <div className="flex items-center justify-between border-b border-slate-100 p-4 text-slate-800">
 <div className="flex items-center gap-2">
 <Package className="h-4 w-4 text-emerald-600" />
 <span>Danh mục vật tư</span>
 </div>
 <span className="text-slate-400 tracking-widest">{request.items?.length || 0} items</span>
 </div>
 <table className="w-full text-left sm:text-sm text-xs">
 <thead>
 <tr className="border-b border-slate-50 bg-slate-50/30 text-slate-400 tracking-wider">
 <th className="p-4 font-normal">Vật tư / Mã</th>
 <th className="p-4 text-right font-normal">Yêu cầu</th>
 <th className="p-4 text-right font-normal">Đã cấp</th>
 <th className="p-4 font-normal">Ghi chú</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-50">
 {request.items?.map((item: any) => (
 <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
 <td className="p-4">
 <p className="text-slate-800">{item.inventory_item?.name}</p>
 <p className="text-slate-400">{item.inventory_item?.code}</p>
 </td>
 <td className="p-4 text-right text-slate-900">
 {item.requested_quantity} {item.unit || item.inventory_item?.unit}
 </td>
 <td className="p-4 text-right">
 <span className={cn('', item.issued_quantity > 0 ? 'text-emerald-600' : 'text-slate-300')}>
 {item.issued_quantity || 0}
 </span>
 </td>
 <td className="p-4 sm:text-sm text-xs text-slate-500 italic">
 {item.note || '-'}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </section>
 </div>

 {/* Sidebar Info */}
 <div className="md:col-span-4 space-y-4">
 <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
 <h3 className="text-slate-400 tracking-widest">Phê duyệt</h3>
 
 <div className="space-y-3">
 <div className="flex items-center justify-between">
 <span className="sm:text-sm text-xs text-slate-500">Đ" ưu tiên</span>
 <span className={cn('px-2 py-0.5 rounded ', priorityMap[request.priority as keyof typeof priorityMap].color)}>
 {priorityMap[request.priority as keyof typeof priorityMap].label}
 </span>
 </div>
 <div className="flex items-center justify-between">
 <span className="sm:text-sm text-xs text-slate-500">Người phê duyệt</span>
 <span className="sm:text-sm text-xs text-slate-700">{request.approver?.full_name || 'Đangốchờ...'}</span>
 </div>
 {request.approved_at && (
 <div className="flex items-center justify-between">
 <span className="sm:text-sm text-xs text-slate-500">Ngày phê duyệt</span>
 <span className="sm:text-sm text-xs text-slate-700">{new Date(request.approved_at).toLocaleDateString('vi-VN')}</span>
 </div>
 )}
 </div>
 </section>

 <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
 <h3 className="text-slate-400 tracking-widest">L9ch sử</h3>
 <div className="space-y-4">
 <div className="flex gap-3">
 <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
 <div className="space-y-1">
 <p className="text-slate-700">Đã gửi yêu cầu</p>
 <p className="text-slate-400">{new Date(request.created_at).toLocaleString('vi-VN')}</p>
 </div>
 </div>
 {request.status_code === 'approved' && (
 <div className="flex gap-3">
 <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
 <div className="space-y-1">
 <p className="text-slate-700">Đã được phê duyệt</p>
 <p className="text-slate-400">{new Date(request.approved_at).toLocaleString('vi-VN')}</p>
 </div>
 </div>
 )}
 </div>
 </section>

 <div className="rounded-md bg-emerald-50 p-4 text-center">
 <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-emerald-600">
 <ShieldCheck className="h-5 w-5" />
 </div>
 <p className="sm:text-sm text-xs text-emerald-800">Dữ liệu an toàn</p>
 <p className="text-emerald-600">Yêu cầu này được bảo mật và lưu vết thay "i theo chuẩn ISO.</p>
 </div>
 </div>
 </div>

 <ConfirmDialog
 isOpen={confirmOpen}
 title="Xác nhận phê duyệt"
 description={`Bạn có chắc chắn muốn phê duyệt yêu cầu vật tư "${request.title}"? Thao tác này sẽ cho phép kho xuất vật tư.`}
 confirmLabel="Phê duyệt ngay"
 variant="primary"
 onConfirm={() => {
 approve();
 setConfirmOpen(false);
 }}
 onClose={() => setConfirmOpen(false)}
 />
 </div>
 );
}

